import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template151: Île de la Cité (Notre Dame, Sainte-Chapelle, Pont Neuf). Todo desde backend.
// page.texts[0]=header, [1]=intro, [2]=subtitle portada | page.sections | page.llegar | page.images, page.canvasImages
const Template151 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const logoImage = getCanvasImageUrl("logo");
	const portadaImage = getImageSrc("portada");
	const portadaSticker = getCanvasImageUrl("notre-dame-sticker");
	const introContent = page?.texts?.[1]?.content;
	const subtitle = page?.texts?.[2]?.content;
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const llegar = page?.llegar ?? page?.data?.llegar ?? page?.data?.data?.llegar;
	const bouquinistesImage = getImageSrc("bouquinistes");

	const actualHeaderColor = headerBackgroundColor || "#2C3E50";
	const contentColor = textColor || "#000";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 1,
					}}
				>
					{headerText}
					{!isSmallScreen && logoImage && (
						<img
							src={logoImage}
							alt=""
							style={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{portadaImage && (
				<Box data-block-id="image-0" sx={{ position: "relative" }}>
					<img
						src={portadaImage}
						alt=""
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
					{subtitle && (
						<Box
							component="h1"
							sx={{
								fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
								fontSize: isSmallScreen ? "28px" : "32px",
								textAlign: "center",
								position: "absolute",
								bottom: "0",
								margin: "10px",
								left: "20px",
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							}}
						>
							{subtitle}
						</Box>
					)}
					{portadaSticker && (
						<img
							src={portadaSticker}
							alt=""
							style={{
								width: 90,
								position: "absolute",
								bottom: 60,
								left: 18,
							}}
						/>
					)}
				</Box>
			)}

			<Box
				data-block-id="text-1"
				sx={{
					padding: "10px 12px",
					paddingBottom: "5px",
					position: "relative",
				}}
			>
				{introContent && (
					<Box component="p" sx={{ ...estiloP, marginTop: "-10px" }}>
						{introContent}
					</Box>
				)}

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						marginTop: "10px",
					}}
				>
					{sections.map((section, idx) => {
						const imageSrc =
							section.layout === "box"
								? section.imageCanvasId
									? getCanvasImageUrl(section.imageCanvasId)
									: getImageSrc(section.imageId)
								: getImageSrc(section.imageId);

						if (section.layout === "box") {
							return (
								<Box
									key={idx}
									data-block-id={`text-${idx + 2}`}
									sx={{ display: "flex", gap: "16px", position: "relative" }}
								>
									<Box sx={{ width: "100%" }}>
										<Box
											component="p"
											sx={{
												...estiloP,
												padding: "12px",
												backgroundColor: "#E8F4F8",
												borderRadius: "12px",
											}}
										>
											{imageSrc && (
												<img
													src={imageSrc}
													alt={section.title || ""}
													style={{
														width: isSmallScreen ? 105 : 115,
														float: "right",
														marginRight: -34,
														marginTop: -40,
														borderRadius: 12,
													}}
												/>
											)}
											{section.title && (
												<Box component="h3" sx={estiloH3}>
													{section.title}
												</Box>
											)}
											{section.body}
										</Box>
									</Box>
									{section.sabiasQue?.[0] && (
										<Box
											sx={{
												position: "absolute",
												top: 0,
												left: isSmallScreen ? "52%" : "40%",
											}}
										>
											<SabiasQue
												icono={section.sabiasQue[0].icono}
												texto={section.sabiasQue[0].texto}
											/>
										</Box>
									)}
									{section.sabiasQue?.[1] && (
										<Box
											sx={{
												position: "absolute",
												top: 0,
												left: isSmallScreen ? "64%" : "50%",
											}}
										>
											<SabiasQue
												icono={section.sabiasQue[1].icono}
												texto={section.sabiasQue[1].texto}
											/>
										</Box>
									)}
									{section.sabiasQue?.[2] && (
										<Box sx={{ position: "absolute", top: 32, left: "62%" }}>
											<SabiasQue
												icono={section.sabiasQue[2].icono}
												texto={section.sabiasQue[2].texto}
											/>
										</Box>
									)}
								</Box>
							);
						}

						return (
							<Box
								key={idx}
								data-block-id={`text-${idx + 2}`}
								sx={{ display: "flex", gap: "16px", position: "relative" }}
							>
								{imageSrc && (
									<img
										src={imageSrc}
										alt={section.title || ""}
										style={{
											width: "40%",
											height: 110,
											objectFit: "cover",
											borderRadius: 12,
										}}
									/>
								)}
								{section.sabiasQue?.[0] && (
									<Box sx={{ position: "absolute", top: -10, left: "32%" }}>
										<SabiasQue
											icono={section.sabiasQue[0].icono}
											texto={section.sabiasQue[0].texto}
										/>
									</Box>
								)}
								{section.sabiasQue?.[1] && (
									<Box sx={{ position: "absolute", top: 30, left: "32%" }}>
										<SabiasQue
											icono={section.sabiasQue[1].icono}
											texto={section.sabiasQue[1].texto}
										/>
									</Box>
								)}
								{section.sabiasQue?.[2] && (
									<Box sx={{ position: "absolute", top: 32, left: "32%" }}>
										<SabiasQue
											icono={section.sabiasQue[2].icono}
											texto={section.sabiasQue[2].texto}
										/>
									</Box>
								)}
								<Box sx={{ width: "60%" }}>
									{section.title && (
										<Box component="h3" sx={estiloH3}>
											{section.title}
										</Box>
									)}
									{section.body && (
										<Box component="p" sx={estiloP}>
											{section.body}
										</Box>
									)}
								</Box>
							</Box>
						);
					})}

					{llegar?.title && (
						<Box
							sx={{
								backgroundColor: "#E8F4F8",
								border: "3px solid #2C3E50",
								borderRadius: "12px",
								padding: "8px 20px",
								fontSize: 13,
								fontFamily: `${
									fontFamily || "'Poppins', sans-serif"
								} !important`,
								color: "#333",
								marginBottom: isSmallScreen ? "12px" : 0,
								position: "relative",
							}}
						>
							<Box
								component="h4"
								sx={{
									margin: 0,
									marginBottom: "8px",
									fontSize: 13,
									fontWeight: 700,
									display: "flex",
									alignItems: "center",
									gap: "6px",
									color: "#2C3E50",
									textTransform: "uppercase",
								}}
							>
								{llegar.title}
							</Box>
							{llegar.content && (
								<Box
									component="div"
									dangerouslySetInnerHTML={{ __html: llegar.content }}
									sx={{
										paddingLeft: 0,
										margin: 0,
										lineHeight: 1.5,
										"& ul": { paddingLeft: "20px", margin: 0 },
									}}
								/>
							)}
							{bouquinistesImage && (
								<img
									src={bouquinistesImage}
									alt=""
									style={{
										width: 100,
										position: "absolute",
										bottom: isSmallScreen ? -100 : -5,
										right: -10,
									}}
								/>
							)}
							{llegar.tooltipBox?.text && (
								<Box
									sx={{
										position: "absolute",
										bottom: isSmallScreen ? -60 : 50,
										right: isSmallScreen ? 60 : 30,
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 0px 16px",
										padding: "10px 12px",
										fontSize: 11,
										fontFamily: `${
											fontFamily || "'Poppins', sans-serif"
										} !important`,
										color: contentColor,
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: isSmallScreen ? 220 : 160,
									}}
								>
									{llegar.tooltipBox.text}
								</Box>
							)}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template151.thumbnail = "/images/template-1.png";

export default Template151;
